/**
 * INTERRUPTOR da pele v2 (redesign premium).
 *
 * O redesign foi montado EM PARALELO, atrás deste interruptor, sem tocar na
 * aparência antiga. Desde o GO-LIVE (24/07/26) a v2 é o padrão **da M5** e da
 * **Vitrine — Obras**, que é o showroom do mesmo produto: demo e prova ao vivo
 * têm que mostrar a mesma cara do sistema real, senão a Vitrine deixa de provar
 * o que está no ar. **31/07/26: entra o ÁTOMO** — go-live do tenant deles,
 * precedido de auditoria das telas que eles usam e de baseline de invariantes
 * pra comparar depois. Regula360 e a Vitrine Regula continuam na v1 até cada
 * uma ter o próprio go-live. A v1 segue intacta e acessível em qualquer empresa por `?skin=v1`.
 *
 * IMPORTANTE: isto troca APENAS aparência (tokens de cor/estilo em CSS). NÃO
 * altera rota, clique, query, fluxo ou qualquer comportamento — o esqueleto do
 * sistema é o mesmo nas duas peles.
 *
 * Ordem de decisão (a primeira que responder ganha):
 *   1. `?skin=v1|v2` na URL  → manda e memoriza (é a saída de emergência e o
 *      jeito de testar a v2 em QUALQUER empresa, inclusive a Vitrine)
 *   2. última escolha memorizada PARA AQUELA EMPRESA (`mrg-skin:<slug>`)
 *   3. a EMPRESA ATIVA: v2 se estiver em EMPRESAS_V2, senão v1
 *
 * Reverter o go-live da M5 = tirar "m5group" de EMPRESAS_V2 (1 linha). Levar a
 * v2 pra outro tenant = acrescentar o slug dele na mesma lista.
 *
 * 🔴 A CHAVE ERA DO NAVEGADOR INTEIRO — e vazava entre tenants (achado da
 * auditoria de perdas v1→v2, 28/07). Reproduzido: abrir a **Vitrine** com
 * `?skin=v2`, trocar pro **Átomo** no seletor → o Átomo abria na **v2**. Um
 * tenant de terceiro estreando um redesign que ninguém decidiu entregar pra
 * ele, sobrevivendo a logout, por causa de uma URL que alguém abriu uma vez.
 *
 * Duas coisas seguravam o vazamento e as duas eram furadas:
 *   · a memória (`mrg-skin`) era GLOBAL — agora é por empresa (`mrg-skin:<slug>`)
 *   · trocar de empresa recarregava a MESMA URL, com o `?skin=v2` ainda nela —
 *     e o parâmetro vence a memória, então só escopar a chave não bastaria.
 *     Ver `urlSemPeleForcada` / `recarregarSemPeleForcada` no fim do arquivo.
 *
 * Fica urgente com o flip do Átomo na sexta: o tenant deles não pode estrear
 * numa pele por acidente de navegador.
 */
const STORAGE_PREFIX = "mrg-skin:";

/**
 * A chave GLOBAL antiga. Só continua citada aqui pra ser **APAGADA** no boot.
 *
 * De propósito NÃO migramos o valor dela pra empresa atual: a migração rodaria
 * no boot que vem DEPOIS da troca de empresa — ou seja, carimbaria a escolha
 * feita na Vitrine em cima do Átomo, reproduzindo o vazamento exatamente uma
 * última vez. Quem tinha v2 na mão numa empresa que já é v2 não perde nada;
 * quem tinha na mão num tenant v1 volta pro padrão do tenant, que é o certo.
 */
const STORAGE_KEY_LEGADO = "mrg-skin";

/**
 * Slug da empresa ativa, espelhado em localStorage pelo `useEmpresaAtiva`.
 *
 * POR QUE existir se `m5_empresa_ativa` já guarda a empresa: aquilo é o UUID, e
 * o `applySkin()` roda no BOOT (main.tsx), antes do React montar e MUITO antes
 * de `/me/empresas` responder — no boot não há como traduzir UUID→slug. Então o
 * slug fica gravado ao lado, pro boot decidir a pele sem esperar rede.
 */
import { isOperadorPath } from "./operador";
import { marcaDoTenant } from "./tenant";

export const EMPRESA_SLUG_KEY = "m5_empresa_slug";

export type Skin = "v1" | "v2";

/**
 * Empresas em que a v2 já é o padrão. GO-LIVE 24/07/26: a M5 e a Vitrine do
 * MESMO produto (`vitrine-fac` = "Vitrine — Obras"), que é onde a operação é
 * demonstrada e provada — showroom e sistema real não podem ter caras
 * diferentes. **31/07/26 entrou o Átomo** (go-live do tenant). **21/10/26 entra a
 * REGULA** (`regula360` + o showroom `vitrine-reg`, pelo mesmo motivo do showroom:
 * a demonstração tem que ter a mesma cara do sistema real). Fora desta lista
 * (Operador, ou empresa ainda desconhecida no boot) o padrão é v1 — o lado seguro.
 */
// 31/07/2026 — ENTRA O ÁTOMO. A linha do flip é esta, e é só esta.
// 21/10/2026 — ENTRA A REGULA (decisão do dono: "vamos travar" a pele v2, "só regula"):
// `regula360` e o showroom `vitrine-reg`, para a demonstração ter a mesma cara do sistema real.
// Reverter, se der errado: tirar o slug daqui. Uma linha, um deploy.
const EMPRESAS_V2: readonly string[] = ["m5group", "vitrine-fac", "atomo", "regula360", "vitrine-reg"];

/** Pele padrão de uma empresa, pelo slug. Pura — é o coração da regra. */
export function peleDaEmpresa(slug: string | null | undefined): Skin {
  return EMPRESAS_V2.includes((slug ?? "").trim().toLowerCase()) ? "v2" : "v1";
}

/**
 * A chave de memória DAQUELA empresa. Sem slug (boot de navegador novo, tela de
 * login) não há onde guardar — e não guardar é o lado seguro: a escolha da URL
 * ainda vale pra carga atual, mas não sobra pra ninguém.
 */
function chaveDaPele(slug: string | null | undefined): string | null {
  const s = (slug ?? "").trim().toLowerCase();
  return s ? STORAGE_PREFIX + s : null;
}

function lerSkinSalvada(slug: string | null | undefined): Skin | null {
  const chave = chaveDaPele(slug);
  if (!chave) return null;
  try {
    const saved = localStorage.getItem(chave);
    return saved === "v1" || saved === "v2" ? saved : null;
  } catch {
    // localStorage indisponível — segue pro próximo critério
    return null;
  }
}

/** Apaga a chave global antiga. Idempotente; roda a cada boot. */
function esquecerPeleGlobal(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_LEGADO);
  } catch {
    // storage bloqueado — nada a esquecer
  }
}

function lerSkinDaUrl(): Skin | null {
  try {
    const param = new URLSearchParams(window.location.search).get("skin");
    return param === "v1" || param === "v2" ? param : null;
  } catch {
    return null;
  }
}

function lerSlugDaEmpresa(): string | null {
  try {
    return localStorage.getItem(EMPRESA_SLUG_KEY);
  } catch {
    return null;
  }
}

/**
 * A pele que DEVERIA estar no ar agora, pelos insumos atuais (URL + memória +
 * empresa ativa persistida). Comparar isto com `peleAtual()` diz se o boot
 * decidiu com informação velha — ver `useEmpresaAtiva`.
 */
export function peleDesejada(): Skin {
  if (typeof window === "undefined") return "v1";
  const slug = lerSlugDaEmpresa();
  return lerSkinDaUrl() ?? lerSkinSalvada(slug) ?? peleDaEmpresa(slug);
}

export function applySkin(): Skin {
  if (typeof window === "undefined") return "v1";

  esquecerPeleGlobal();

  // Query param persiste a escolha (facilita testar ao vivo sem repetir a URL)
  // — mas só NA EMPRESA em que foi feita.
  const param = lerSkinDaUrl();
  if (param) {
    const chave = chaveDaPele(lerSlugDaEmpresa());
    if (chave) {
      try {
        localStorage.setItem(chave, param);
      } catch {
        // ignora
      }
    }
  }

  // 👑 Boot direto numa rota da plataforma: a cara é a da CASA, não a do
  // tenant que estava na aba — ver `aplicarPeleDaRota`, que é quem manda.
  if (isOperadorPath(window.location.pathname)) {
    aplicarPeleDaRota(window.location.pathname);
    return "v2";
  }

  const skin = peleDesejada();
  document.documentElement.setAttribute("data-skin", skin);
  return skin;
}

/**
 * 👑 A CAMADA PLATAFORMA TEM UMA CARA — decisão do dono, 26/08/2026 (R2).
 *
 * Até aqui o Operador HERDAVA a pele do tenant ativo na aba: a MESMA tela de
 * plataforma abria com ouro velho (#C9A55C, v1) ou ouro novo (#C8841E, v2),
 * radius 8 ou 6, dependendo de qual empresa o dono tinha visitado por último.
 * A casa de máquinas não tinha cara própria — emprestava a de quem estivesse
 * na aba. Medido no inventário de 26/08: nenhum arquivo de `/operador/*`
 * consultava a pele; o `data-skin` era o que o boot tivesse deixado.
 *
 * ⇒ Em rota da plataforma (`isOperadorPath`): SEMPRE v2 + marca da casa (m5).
 *   O `?skin=` da URL não vale aqui de propósito — identidade não é preferência.
 * ⇒ Ao voltar pra camada empresa, restaura a pele do tenant (a mesma conta do
 *   boot) e a marca dele (âmbar M5 × navy Regula).
 *
 * Chamado pelo Layout a cada troca de rota — o boot (`applySkin`) cobre a
 * entrada direta por URL; este cobre a navegação SPA, que não recarrega.
 */
export function aplicarPeleDaRota(pathname: string): void {
  if (typeof document === "undefined") return;
  if (isOperadorPath(pathname)) {
    document.documentElement.setAttribute("data-skin", "v2");
    document.documentElement.setAttribute("data-brand", "m5");
    return;
  }
  document.documentElement.setAttribute("data-skin", peleDesejada());
  document.documentElement.setAttribute("data-brand", marcaDoTenant(lerSlugDaEmpresa()));
}

/**
 * A MESMA URL, sem o `?skin=` forçado. Pura pra ser testável.
 *
 * Existe porque `trocar()` recarregava a página com `window.location.reload()`,
 * que repete a URL inteira — inclusive um `?skin=v2` digitado quando a aba
 * ainda estava em OUTRA empresa. Como o parâmetro vence a memória, escopar a
 * chave por empresa sozinho NÃO fecharia o vazamento: o Átomo continuaria
 * abrindo na v2 herdada da Vitrine.
 */
export function urlSemPeleForcada(href: string): string {
  try {
    const u = new URL(href);
    if (!u.searchParams.has("skin")) return href;
    u.searchParams.delete("skin");
    return u.toString();
  } catch {
    return href;
  }
}

/**
 * Recarrega a página largando a pele forçada na URL. É o que `trocar()` usa:
 * a empresa nova estreia com a pele DELA, não com a que a anterior tinha na
 * barra de endereço. Sem `?skin=` na URL, recarrega igual antes.
 */
export function recarregarSemPeleForcada(): void {
  if (typeof window === "undefined") return;
  const alvo = urlSemPeleForcada(window.location.href);
  if (alvo !== window.location.href) window.location.replace(alvo);
  else window.location.reload();
}

/**
 * Espelha o slug da empresa ativa pro próximo boot. Chamado pelo
 * `useEmpresaAtiva` quando a empresa carrega e ANTES do reload da troca de
 * empresa — se gravasse depois, a empresa nova nasceria com a pele da antiga.
 *
 * Slug vazio NÃO apaga a chave: durante o carregamento a empresa ativa é null
 * por um instante, e apagar ali derrubaria a pele (e o `peleDesejada()` do
 * useEmpresaAtiva pediria um reload atrás do outro).
 */
export function gravarEmpresaDaPele(slug: string | null | undefined): void {
  if (!slug) return;
  try {
    localStorage.setItem(EMPRESA_SLUG_KEY, slug);
  } catch {
    // storage bloqueado — o boot cai no padrão seguro (v1)
  }
}

/**
 * A pele ativa AGORA (lê o atributo que o applySkin gravou no boot). Serve pra
 * decisões de RENDER que só valem na v2 (ex.: itens de menu do redesign que ainda
 * não existem na v1). NÃO muda comportamento da v1 — só revela o que é aditivo.
 * Trocar de pele exige `?skin=v2` na URL (recarrega e reroda o applySkin).
 */
export function skinAtiva(): Skin {
  if (typeof document === "undefined") return "v1";
  const attr = document.documentElement.getAttribute("data-skin");
  return attr === "v2" ? "v2" : "v1";
}

/**
 * Qual pele está no ar AGORA — pra quando a bifurcação não dá pra fazer só em
 * CSS (layout diferente, não só cor).
 *
 * Lê do <html>, que o applySkin() já carimbou antes do React montar. Não é
 * estado reativo de propósito: trocar de pele exige recarregar a página (é o que
 * o ?skin=v2 faz), então não existe o caso de mudar no meio da renderização.
 */
/**
 * Carimba a MARCA do tenant no <html> — é ela que escolhe o acento da pele v2
 * (âmbar M5 × navy Regula). Chamado quando a empresa ativa carrega ou troca.
 *
 * Só afeta a v2: sob a pele v1 o seletor não casa e nada muda.
 */
export function aplicarMarca(marca: "m5" | "regula"): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-brand", marca);
}

export function peleAtual(): Skin {
  if (typeof document === "undefined") return "v1";
  return document.documentElement.getAttribute("data-skin") === "v2" ? "v2" : "v1";
}
